/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useContext, useMemo} from 'react'
import {GoogleTagManagerContext} from '../google-tag-manager/contexts'
import {BRAND_LABEL} from '../../brand'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {breakpoints} from '../../theme/_utils'

const googleTagManagerBodyScript = (id) =>
    `(function(w, d, s, l, i) {
            w[l] = w[l] || []
            w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
            var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : ''
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl

    f.parentNode.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', '${id}')`

const googleTagManagerHeadNoScript = (id) =>
    `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`

export const useGoogleTagManager = () => {
    const gtma = useContext(GoogleTagManagerContext)
    const config = gtma._config
    const id = config.id
    const {locale} = useMultiSite()
    const {data: customer} = useCurrentCustomer()

    const enableEmptyPageViewEvent = config.analyticsEnableFirstEmptyEvent // Changes the default events activation order and import
    const positionZeroEventName = enableEmptyPageViewEvent ? 'startPageview' : 'realPageview'

    /**
     * Appends GTM head noscript
     *
     * @returns {void}
     */
    const setupBodyNoScript = () => {
        const script = document.createElement('noscript')
        script.setAttribute('data-testid', 'gtm-body-script')
        // script.setAttribute('nonce', 'bXktc2VjdXJlLW5vbmNl')
        script.innerHTML = googleTagManagerHeadNoScript(id)
        document.body.appendChild(script)
    }

    /**
     * Appends GTM body script
     *
     * @returns {void}
     */
    const setupHeadScript = () => {
        const script = document.createElement('script')
        script.setAttribute('data-testid', 'gtm-head-script')
        // script.setAttribute('nonce', 'bXktc2VjdXJlLW5vbmNl')
        script.innerHTML = googleTagManagerBodyScript(id)
        document.head.appendChild(script)
    }

    /**
     * Add layer
     *
     * @param {object} data - Arguments
     * @param {function} builder - Dedicated builder function for the arguments
     * @returns {object} - sent object
     */
    const addLayer = async (args, builder) => {
        if (id) {
            window.dataLayer = window.dataLayer || []
            await gtma.addLayer(builder ? builder({...args}) : args)
        }
    }

    /**
     *
     * Method to insert the page view with all the data always at position 0 of the dataLayer array
     * for backward compatibility with MCP (Marketing Cloud) which always reads from this position
     */
    const addLayerAtPositionZero = async (args, builder) => {
        if (id) {
            const data = builder ? await builder({...args}) : args

            window.dataLayer = window.dataLayer || []

            if (window.dataLayer?.[0]?.event === positionZeroEventName) {
                window.dataLayer[0] = data
            } else {
                await gtma.addLayer(builder ? builder({...args}) : args)

                const index = window.dataLayer.findIndex(
                    (item) => item.event === positionZeroEventName,
                )
                if (index > -1) {
                    const [gtmJsElement] = window.dataLayer.splice(index, 1)
                    window.dataLayer.unshift(gtmJsElement)
                }
            }

            /**
             * added new variable because, in the future, MCP will need to read the correct data from here and not from position zero
             */
            window._dataMCP = data
            return data
        }
    }

    const self = useMemo(
        () => ({
            ...config,
            addLayer: addLayer.bind(this, {
                locale,
                isGuest: customer.isGuest,
                customerId: customer.customerId,
                isRegistered: customer.isRegistered,
                brand: BRAND_LABEL,
            }),
            addLayerAtPositionZero: addLayerAtPositionZero.bind(this, {
                locale,
                isGuest: customer.isGuest,
                customerId: customer.customerId,
                isRegistered: customer.isRegistered,
                brand: BRAND_LABEL,
            }),
            setupBodyNoScript,
            setupHeadScript,
            gtma,
            id,
            enableEmptyPageViewEvent,
            positionZeroEventName,
        }),
        [customer],
    )

    return self
}

export const windowGetDevice = (width) => {
    if (width < parseInt(breakpoints.md)) return 'm'
    else if (width < parseInt(breakpoints.lg)) return 't'
    else if (width < parseInt(breakpoints.xl)) return 'd'
    else return 'd'
}
